import * as React from 'react';
import { CHART_COLORS, CHART_INK, formatCompact, formatPercent } from './chart-utils';

export type StatTileDelta = {
  value: number;
  /** e.g. "vs previous 30 days" */
  label: string;
  upIsGood?: boolean;
  format?: 'number' | 'percent';
};

export type StatTileProps = {
  label: string;
  value: number | string | null;
  format?: 'number' | 'percent';
  delta?: StatTileDelta;
  hint?: string;
  /** Optional 12-point sparkline. */
  trend?: number[];
  className?: string;
};

const GOOD = '#1a7f37';
const BAD = '#c62828';

function formatValue(value: number | string | null, format: 'number' | 'percent'): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return format === 'percent' ? formatPercent(value) : formatCompact(value);
}

function formatDelta(delta: StatTileDelta): string {
  const format = delta.format ?? 'number';
  const abs = Math.abs(delta.value);
  const body = format === 'percent' ? formatPercent(abs) : formatCompact(abs);
  if (delta.value > 0) return `+${body}`;
  if (delta.value < 0) return `−${body}`;
  return body;
}

function Sparkline({ points }: { points: number[] }) {
  const width = 72;
  const height = 24;
  if (points.length < 2) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((p, i) => [i * step, height - ((p - min) / span) * (height - 4) - 2] as const);
  const d = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg
      data-testid="stat-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      className="shrink-0"
    >
      <path d={d} fill="none" stroke={CHART_INK.muted} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r={3} fill={CHART_COLORS[0]} stroke={CHART_INK.surface} strokeWidth={2} />
    </svg>
  );
}

/** KPI tile: label, headline value, optional signed delta vs a named period. */
export function StatTile({ label, value, format = 'number', delta, hint, trend, className }: StatTileProps) {
  const deltaColor =
    delta && delta.value !== 0
      ? (delta.value > 0) === (delta.upIsGood ?? true)
        ? GOOD
        : BAD
      : CHART_INK.muted;

  return (
    <div
      data-testid="stat-tile"
      className={['rounded-xl border border-gray-200 bg-white px-4 py-3', className].filter(Boolean).join(' ')}
    >
      <div className="text-xs font-medium" style={{ color: CHART_INK.secondary }}>
        {label}
      </div>
      <div className="mt-1 flex items-end justify-between gap-3">
        <div
          data-testid="stat-value"
          className="font-semibold leading-none"
          style={{ fontSize: 28, color: CHART_INK.primary, fontVariantNumeric: 'normal' }}
        >
          {formatValue(value, format)}
        </div>
        {trend ? <Sparkline points={trend} /> : null}
      </div>
      {delta ? (
        <div className="mt-1.5 flex items-baseline gap-1.5" style={{ fontSize: 12 }}>
          <span data-testid="stat-delta" className="font-medium" style={{ color: deltaColor }}>
            {formatDelta(delta)}
          </span>
          <span style={{ color: CHART_INK.muted }}>{delta.label}</span>
        </div>
      ) : null}
      {hint ? (
        <div className="mt-1" style={{ fontSize: 12, color: CHART_INK.muted }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
